/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

/**
 * Centralized logging service for the NoorWriter web application
 * Supports multiple logging targets including console, Application Insights, and remote logging
 */

// Application Insights interface
interface ApplicationInsights {
  trackTrace(telemetry: {
    message: string
    severityLevel: number
    properties?: Record<string, unknown>
  }): void
  trackException(telemetry: {
    exception: Error
    properties?: Record<string, unknown>
  }): void
}

// Extend Window interface to include Application Insights
declare global {
  interface Window {
    appInsights?: ApplicationInsights
  }
}

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  FATAL = 4
}

export interface LogEntry {
  level: LogLevel
  message: string
  timestamp: Date
  category?: string
  userId?: string
  sessionId?: string
  metadata?: Record<string, unknown>
  error?: Error
}

export interface LogTarget {
  name: string
  minLevel: LogLevel
  log(entry: LogEntry): Promise<void> | void
}

/**
 * Console logging target for development
 */
class ConsoleLogTarget implements LogTarget {
  name = 'console'
  minLevel: LogLevel

  constructor(minLevel: LogLevel = LogLevel.DEBUG) {
    this.minLevel = minLevel
  }

  log(entry: LogEntry): void {
    if (entry.level < this.minLevel) return

    const timestamp = entry.timestamp.toISOString()
    const category = entry.category ? `[${entry.category}]` : ''
    const prefix = `${timestamp} ${category}`

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(prefix, entry.message, entry.metadata)
        break
      case LogLevel.INFO:
        console.info(prefix, entry.message, entry.metadata)
        break
      case LogLevel.WARN:
        console.warn(prefix, entry.message, entry.metadata)
        break
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(prefix, entry.message, entry.error || entry.metadata)
        break
    }
  }
}

/**
 * Application Insights logging target for production
 */
class ApplicationInsightsTarget implements LogTarget {
  name = 'applicationInsights'
  minLevel: LogLevel
  private appInsights: ApplicationInsights | null = null

  constructor(minLevel: LogLevel = LogLevel.INFO) {
    this.minLevel = minLevel
    this.initializeAppInsights()
  }

  private initializeAppInsights(): void {
    // Initialize Application Insights if available
    // This would be configured with your Application Insights connection string
    try {
      if (typeof window !== 'undefined' && window.appInsights) {
        this.appInsights = window.appInsights
      }
    } catch {
      // Silently fail if Application Insights is not available
    }
  }

  log(entry: LogEntry): void {
    if (entry.level < this.minLevel || !this.appInsights) return

    const properties = {
      category: entry.category,
      userId: entry.userId,
      sessionId: entry.sessionId,
      ...entry.metadata
    }

    switch (entry.level) {
      case LogLevel.DEBUG:
      case LogLevel.INFO:
        this.appInsights.trackTrace({
          message: entry.message,
          severityLevel: entry.level === LogLevel.DEBUG ? 0 : 1,
          properties
        })
        break
      case LogLevel.WARN:
        this.appInsights.trackTrace({
          message: entry.message,
          severityLevel: 2,
          properties
        })
        break
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        if (entry.error) {
          this.appInsights.trackException({
            exception: entry.error,
            properties: { ...properties, message: entry.message }
          })
        } else {
          this.appInsights.trackTrace({
            message: entry.message,
            severityLevel: entry.level === LogLevel.ERROR ? 3 : 4,
            properties
          })
        }
        break
    }
  }
}

/**
 * Remote logging target for sending logs to backend
 */
class RemoteLogTarget implements LogTarget {
  name = 'remote'
  minLevel: LogLevel
  private endpoint: string
  private buffer: LogEntry[] = []
  private flushInterval: number

  constructor(endpoint: string, minLevel: LogLevel = LogLevel.WARN) {
    this.endpoint = endpoint
    this.minLevel = minLevel
    
    // Flush logs every 30 seconds
    this.flushInterval = window.setInterval(() => this.flush(), 30000)
    
    // Flush logs before page unload
    window.addEventListener('beforeunload', () => this.flush())
  }

  log(entry: LogEntry): void {
    if (entry.level < this.minLevel) return
    
    this.buffer.push(entry)
    
    // Flush immediately for errors and fatal logs
    if (entry.level >= LogLevel.ERROR) {
      this.flush()
    }
  }

  private async flush(): Promise<void> {
    if (this.buffer.length === 0) return

    const logs = [...this.buffer]
    this.buffer = []

    try {
      await globalThis.fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ logs })
      })
    } catch (error) {
      // If remote logging fails, fall back to console
      console.warn('Failed to send logs to remote endpoint:', error)
    }
  }

  destroy(): void {
    clearInterval(this.flushInterval)
    this.flush()
  }
}

/**
 * Main logger class
 */
class Logger {
  private targets: LogTarget[] = []
  private defaultCategory = 'App'
  private userId?: string
  private sessionId?: string

  constructor() {
    this.initializeDefaultTargets()
  }

  private initializeDefaultTargets(): void {
    const isDevelopment = import.meta.env.DEV
    
    if (isDevelopment) {
      // In development, log everything to console
      this.addTarget(new ConsoleLogTarget(LogLevel.DEBUG))
    } else {
      // In production, only log warnings and errors to console
      this.addTarget(new ConsoleLogTarget(LogLevel.WARN))
      
      // Add Application Insights for production logging
      this.addTarget(new ApplicationInsightsTarget(LogLevel.INFO))
      
      // Add remote logging for critical errors
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
      if (apiBaseUrl) {
        this.addTarget(new RemoteLogTarget(`${apiBaseUrl}/api/logs`, LogLevel.ERROR))
      }
    }
  }

  addTarget(target: LogTarget): void {
    this.targets.push(target)
  }

  removeTarget(targetName: string): void {
    this.targets = this.targets.filter(target => target.name !== targetName)
  }

  setUserId(userId: string): void {
    this.userId = userId
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId
  }

  setDefaultCategory(category: string): void {
    this.defaultCategory = category
  }

  private log(level: LogLevel, message: string, metadata?: Record<string, unknown>, error?: Error, category?: string): void {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date(),
      category: category || this.defaultCategory,
      userId: this.userId,
      sessionId: this.sessionId,
      metadata,
      error
    }

    this.targets.forEach(target => {
      try {
        target.log(entry)
      } catch (err) {
        // Prevent logging errors from breaking the application
        console.error('Logging target failed:', target.name, err)
      }
    })
  }

  debug(message: string, metadata?: Record<string, unknown>, category?: string): void {
    this.log(LogLevel.DEBUG, message, metadata, undefined, category)
  }

  info(message: string, metadata?: Record<string, unknown>, category?: string): void {
    this.log(LogLevel.INFO, message, metadata, undefined, category)
  }

  warn(message: string, metadata?: Record<string, unknown>, category?: string): void {
    this.log(LogLevel.WARN, message, metadata, undefined, category)
  }

  error(message: string, error?: Error, metadata?: Record<string, unknown>, category?: string): void {
    this.log(LogLevel.ERROR, message, metadata, error, category)
  }

  fatal(message: string, error?: Error, metadata?: Record<string, unknown>, category?: string): void {
    this.log(LogLevel.FATAL, message, metadata, error, category)
  }
}

// Create and export singleton logger instance
export const logger = new Logger()

// Export types and classes for advanced usage
export { Logger, ConsoleLogTarget, ApplicationInsightsTarget, RemoteLogTarget }
